import { useMemo } from 'react';
import { ZodSchema } from 'zod';

const getLocalStorageActions = <T>(key: string) => ({
    getUnsafe: () => {
        try {
            const stringValue = window.localStorage.getItem(key);
            return stringValue ? (JSON.parse(stringValue) as T) : null;
        } catch (error) {
            return null;
        }
    },

    set: (value: T) => {
        try {
            window.localStorage.setItem(key, JSON.stringify(value));
            return true;
        } catch (error) {
            return false;
        }
    },
    delete: () => {
        try {
            window.localStorage.removeItem(key);
            return true;
        } catch (error) {
            return false;
        }
    },
    hasValue: () => {
        try {
            return !!window.localStorage.getItem(key);
        } catch (error) {
            return false;
        }
    },
});

export const useLocalStorageUnsafe = <T>(key: string) => useMemo(() => getLocalStorageActions<T>(key), [key]);
export const useLocalStorageSafe = <T>(key: string, schema: ZodSchema<T>) =>
    useMemo(
        () => ({
            ...getLocalStorageActions<T>(key),
            getSafe: () => {
                try {
                    const stringValue = window.localStorage.getItem(key);
                    const unsafeValue = stringValue ? JSON.parse(stringValue) : null;

                    const parseResult = schema.safeParse(unsafeValue);
                    return parseResult.success ? parseResult.data : null;
                } catch (error) {
                    return null;
                }
            },
        }),
        [key, schema]
    );
